/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Button, Checkbox, Divider, FormControlLabel, Typography, CircularProgress, Select, MenuItem } from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import { ContactColumns } from "src/modules/contacts/types";
import { useGetContactCustomFieldsApi } from "src/api/customFields";
import { getCurrentCompanyId } from "src/modules/companies/slices";
import { useSelector } from "react-redux";
import { ContactDefaultCustomField } from "src/constants/types";

type MappingScreenProps = {
  csvHeaders: string[];
  onContinue: (columnMapping: ContactColumns, isAccepting: boolean) => void;
  isUploading?: boolean;
  onBack?: () => void;
};

export default function MappingScreen({ csvHeaders, onContinue, isUploading = false, onBack }: MappingScreenProps) {
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const getCustomFields = useGetContactCustomFieldsApi();

  const [columnMapping, setColumnMapping] = useState<ContactColumns>({
    phoneNumber: "",
    firstName: "",
    lastName: "",
    email: "",
    birthdate: "",
    timeZone: "",
    customFields: [],
  });
  const [isAccepting, setIsAccepting] = useState(false);
  const [availableCustomFields, setAvailableCustomFields] = useState<ContactDefaultCustomField[]>([]);
  const [customFieldsLoaded, setCustomFieldsLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load custom fields on component mount
  useEffect(() => {
    const loadCustomFields = async () => {
      if (!selectedCompanyId) return;
      try {
        const response = await getCustomFields({
          companyId: selectedCompanyId,
          customOnly: true,
        });
        setAvailableCustomFields(response.data.items);
      } catch (error) {
        console.error("Failed to load custom fields:", error);
      } finally {
        setCustomFieldsLoaded(true);
      }
    };
    loadCustomFields();
  }, [selectedCompanyId, getCustomFields]);

  const standardFields = [
    { key: "phoneNumber" as keyof ContactColumns, label: "Phone Number" },
    { key: "firstName" as keyof ContactColumns, label: "First Name" },
    { key: "lastName" as keyof ContactColumns, label: "Last Name" },
    { key: "email" as keyof ContactColumns, label: "Email" },
    { key: "birthdate" as keyof ContactColumns, label: "Birthdate" },
    { key: "timeZone" as keyof ContactColumns, label: "Timezone" },
  ];

  const mappingValidationError = useMemo(() => {
    if (csvHeaders.length === 0 || !isInitialized) return null;

    // Get all mapped standard field values
    const standardMappedValues = [
      columnMapping.phoneNumber,
      columnMapping.firstName,
      columnMapping.lastName,
      columnMapping.email,
      columnMapping.birthdate,
      columnMapping.timeZone,
    ].filter((v): v is string => Boolean(v));

    // Get all mapped custom field column names
    const customMappedValues = columnMapping.customFields
      .map(cf => cf.columnName)
      .filter(Boolean);

    const allMappedValues = [...standardMappedValues, ...customMappedValues];
    const mappedSet = new Set(allMappedValues);

    if (!columnMapping.phoneNumber) {
      return "Please map a file column to Phone Number.";
    }

    if (mappedSet.size !== allMappedValues.length) {
      return "Each file column can only be mapped to one platform field.";
    }

    // Ensure all mapped values exist in the file headers
    for (const val of allMappedValues) {
      if (!csvHeaders.includes(val)) {
        return "Mapped columns must exist in the uploaded file.";
      }
    }

    return null;
  }, [columnMapping, csvHeaders]);

  const isMappingValid = !mappingValidationError;

  // Auto-map columns when component mounts or headers change
  React.useEffect(() => {
    if (!isInitialized && csvHeaders.length > 0 && customFieldsLoaded) {
      const auto = autoMapHeaders(csvHeaders, availableCustomFields);
      setColumnMapping(auto);
      setIsInitialized(true);
    }
  }, [csvHeaders, availableCustomFields, customFieldsLoaded, isInitialized]);

  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  // Shared synonyms for standard fields (single source of truth)
  const STANDARD_FIELD_SYNONYMS: {
    phoneNumber: string[];
    firstName: string[];
    lastName: string[];
    email: string[];
    birthdate: string[];
    timeZone: string[];
  } = {
    phoneNumber: [
      "phone",
      "phone number",
      "mobile",
      "mobile number",
      "cell",
      "cellphone",
      "cell phone",
      "telephone",
      "tel",
    ],
    firstName: [
      "first name",
      "firstname",
      "given name",
      "givenname",
      "forename",
      "fname",
    ],
    lastName: [
      "last name",
      "lastname",
      "surname",
      "family name",
      "lname",
    ],
    email: ["email", "e-mail", "mail", "email address"],
    birthdate: [
      "birthdate",
      "birth date",
      "birthday",
      "date of birth",
      "dob",
    ],
    timeZone: ["time zone", "timezone", "time-zone", "tz"],
  };

  // Heuristic to determine if a header likely represents a standard field
  const isLikelyStandardHeader = (header: string) => {
    const h = normalize(header);
    const matchesAny = (synonyms: string[]) => {
      return synonyms.some((syn) => {
        const n = normalize(syn);
        return h === n || h.includes(n);
      });
    };

    const allSynonyms = [
      ...STANDARD_FIELD_SYNONYMS.phoneNumber,
      ...STANDARD_FIELD_SYNONYMS.firstName,
      ...STANDARD_FIELD_SYNONYMS.lastName,
      ...STANDARD_FIELD_SYNONYMS.email,
      ...STANDARD_FIELD_SYNONYMS.birthdate,
      ...STANDARD_FIELD_SYNONYMS.timeZone,
    ];

    return matchesAny(allSynonyms);
  };

  const autoMapHeaders = (headers: string[], customFields: ContactDefaultCustomField[]) => {
    const used = new Set<string>();
    const mapping: ContactColumns = {
      phoneNumber: "",
      firstName: "",
      lastName: "",
      email: "",
      birthdate: "",
      timeZone: "",
      customFields: [],
    };

    const tryMatch = (synonyms: string[], header: string) => {
      const h = normalize(header);
      for (const syn of synonyms) {
        const n = normalize(syn);
        if (h === n || h.includes(n)) return true;
      }
      return false;
    };

    const findHeader = (synonyms: string[]) => {
      for (const h of headers) {
        if (used.has(h)) continue;
        if (tryMatch(synonyms, h)) return h;
      }
      return "";
    };

    const phone = findHeader(STANDARD_FIELD_SYNONYMS.phoneNumber);
    if (phone) used.add(phone), (mapping.phoneNumber = phone);

    const firstName = findHeader(STANDARD_FIELD_SYNONYMS.firstName);
    if (firstName) used.add(firstName), (mapping.firstName = firstName);

    const lastName = findHeader(STANDARD_FIELD_SYNONYMS.lastName);
    if (lastName) used.add(lastName), (mapping.lastName = lastName);

    const email = findHeader(STANDARD_FIELD_SYNONYMS.email);
    if (email) used.add(email), (mapping.email = email);

    const birthdate = findHeader(STANDARD_FIELD_SYNONYMS.birthdate);
    if (birthdate) used.add(birthdate), (mapping.birthdate = birthdate);

    const timeZone = findHeader(STANDARD_FIELD_SYNONYMS.timeZone);
    if (timeZone) used.add(timeZone), (mapping.timeZone = timeZone);

    // Auto-map custom fields by name matching
    for (const header of headers) {
      if (used.has(header)) continue;

      // Find custom field with matching name
      const matchingCustomField = customFields.find(field =>
        normalize(field.name) === normalize(header)
      );

      if (matchingCustomField) {
        mapping.customFields.push({
          fieldName: matchingCustomField.name,
          columnName: header
        });
        used.add(header);
      }
    }

    return mapping;
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Match the columns from your file to the corresponding fields in our system.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ maxHeight: 355, overflow: "auto" }}>
          {(() => {
            const headerEntries = csvHeaders.map((h, i) => ({ header: h, index: i }));
            const isMappedToStandard = (h: string) =>
              Object.entries(columnMapping).some(([key, value]) => key != "customFields" && value == h);
            const standardEntries = headerEntries.filter(({ header }) => isMappedToStandard(header) || isLikelyStandardHeader(header));
            const customEntries = headerEntries.filter(({ header }) => !standardEntries.some(se => se.header === header));

            const renderRow = (header: string, keyIndex: number) => {
              const mappedField = Object.entries(columnMapping).find(([, value]) => value == header);
              const customFieldMapping = columnMapping.customFields.find(cf => cf.columnName == header);

              let platformFieldLabel = "Not mapped";
              if (mappedField) {
                platformFieldLabel = standardFields.find(f => f.key == mappedField[0])?.label || "Unknown field";
              } else if (customFieldMapping) {
                platformFieldLabel = availableCustomFields.find(f => f.name == customFieldMapping.fieldName)?.description ||
                  `${customFieldMapping.fieldName}`;
              }

              return (
                <Box key={keyIndex} sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 150 }}>
                    <Typography variant="subtitle2">
                      {header || "(Unnamed column)"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Maps to: {platformFieldLabel}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Select
                      fullWidth
                      size="small"
                      value={mappedField ? mappedField[0] : customFieldMapping ? `custom:${customFieldMapping.fieldName}` : ""}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        const newMapping = { ...columnMapping };

                        // Clear any existing mappings for this header
                        Object.keys(newMapping).forEach(key => {
                          if (key != 'customFields' && newMapping[key as keyof ContactColumns] == header) {
                            (newMapping as any)[key] = "";
                          }
                        });

                        // Remove this header from customFields if it exists
                        newMapping.customFields = newMapping.customFields.filter(cf => cf.columnName != header);

                        // Set new mapping
                        if (selectedValue) {
                          if ((selectedValue as string).startsWith('custom:')) {
                            const fieldName = (selectedValue as string).replace('custom:', '');
                            newMapping.customFields = [...newMapping.customFields, {
                              fieldName,
                              columnName: header
                            }];
                          } else {
                            if (standardFields.some(f => f.key == selectedValue)) {
                              (newMapping as any)[selectedValue as keyof ContactColumns] = header;
                            }
                          }
                        }

                        setColumnMapping(newMapping);
                      }}
                      displayEmpty
                      renderValue={(val) => {
                        if (!val) return "— Select field —";
                        if ((val as string).startsWith('custom:')) {
                          const fieldName = (val as string).replace('custom:', '');
                          return availableCustomFields.find(f => f.name == fieldName)?.description || `${fieldName}`;
                        }
                        return standardFields.find(f => f.key == val)?.label || (val as string);
                      }}
                    >
                      <MenuItem value="">
                        <em>— Not Mapped —</em>
                      </MenuItem>
                      {standardFields.map(({ key, label }) => (
                        <MenuItem key={`${header}-${key}`} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                      {availableCustomFields.length > 0 && (
                        <Divider />
                      )}
                      {availableCustomFields
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((field) => (
                          <MenuItem key={`${header}-custom-${field.metadataId}`} value={`custom:${field.name}`}>
                            {field.description}
                          </MenuItem>
                        ))}
                    </Select>
                  </Box>
                </Box>
              );
            };

            return (
              <>
                {standardEntries.map(({ header, index }) => renderRow(header, index))}
                {customEntries.map(({ header, index }) => renderRow(header, index))}
              </>
            );
          })()}
        </Box>
      </Box>

      {mappingValidationError && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {mappingValidationError}
        </Typography>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={isAccepting}
            onChange={(event) => setIsAccepting(event.target.checked)}
          />
        }
        label="I confirm that I have received express consent to message the contacts that I am adding."
        sx={{ mb: 3 }}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          {onBack && (
            <Button
              variant="outlined"
              onClick={onBack}
              sx={{ minWidth: "auto" }}
            >
              Back
            </Button>
          )}
        </Box>
        <Button
          variant="contained"
          onClick={() => onContinue(columnMapping, isAccepting)}
          disabled={!isAccepting || !isMappingValid || isUploading || !isInitialized}
        >
          {isUploading ? <CircularProgress size={20} /> : "Continue"}
        </Button>
      </Box>
    </Box>
  );
}
